import { useState } from 'react';
import { formaterIdentite } from '@gsm/partage';
import { useRessource, messageErreur } from '../api/ressource';
import { Bloc, Tableau, type Colonne } from '../composants/Tableau';
import { BandeauAlerte } from '../composants/BandeauAlerte';
import { Pastille } from '../composants/Pastille';
import { Icone } from '../composants/Icone';
import { LIBELLE_STATUT_MANDAT, TON_STATUT_MANDAT } from './Mandats';
import {
  BarreFiltres,
  FiltreChoix,
  FiltreRecherche,
  correspond,
  optionsDistinctes,
} from '../composants/Filtres';

interface Mandat {
  id: string;
  reference: string;
  statut: string;
  client: { nom: string; prenom: string; reference: string };
  profil: { id: string; libelle: string };
  agence: { code: string; libelle: string };
}

/** Dernier trimestre clos : c'est lui que l'on restitue au client. */
export function dernierTrimestreClos(aujourdhui: Date): { annee: number; trimestre: number } {
  const courant = Math.floor(aujourdhui.getMonth() / 3) + 1;
  return courant === 1
    ? { annee: aujourdhui.getFullYear() - 1, trimestre: 4 }
    : { annee: aujourdhui.getFullYear(), trimestre: courant - 1 };
}

const PERIODE_DEFAUT = dernierTrimestreClos(new Date());

export function Reporting() {
  const { donnees, chargement, erreur } = useRessource<Mandat[]>('/mandats?statut=ACTIF');
  const tous = donnees ?? [];

  const [annee, setAnnee] = useState(String(PERIODE_DEFAUT.annee));
  const [trimestre, setTrimestre] = useState(String(PERIODE_DEFAUT.trimestre));

  const [recherche, setRecherche] = useState('');
  const [profil, setProfil] = useState('');
  const [agence, setAgence] = useState('');
  const filtresActifs = recherche !== '' || profil !== '' || agence !== '';
  const mandats = tous.filter(
    (m) =>
      correspond(recherche, m.reference, m.client.nom, m.client.prenom, m.client.reference) &&
      (!profil || m.profil.id === profil) &&
      (!agence || m.agence.code === agence),
  );

  function ouvrirReleve(mandatId: string) {
    window.open(`/releve/${mandatId}?annee=${annee}&trimestre=${trimestre}`, '_blank', 'noopener');
  }

  const colonnes: Colonne<Mandat>[] = [
    { cle: 'reference', entete: 'Mandat', num: true, rendu: (m) => m.reference },
    {
      cle: 'client',
      entete: 'Client',
      rendu: (m) => formaterIdentite(m.client.nom, m.client.prenom),
    },
    { cle: 'profil', entete: 'Profil', rendu: (m) => m.profil.libelle },
    {
      cle: 'statut',
      entete: 'Statut',
      rendu: (m) => (
        <Pastille ton={TON_STATUT_MANDAT[m.statut] ?? 'neutre'}>
          {LIBELLE_STATUT_MANDAT[m.statut] ?? m.statut}
        </Pastille>
      ),
    },
    {
      cle: 'action',
      entete: '',
      num: true,
      rendu: (m) => (
        <div className="rangee-actions rangee-actions--droite">
          <button
            type="button"
            className="bouton bouton--icone bouton--compact"
            onClick={() => ouvrirReleve(m.id)}
          >
            <Icone nom="reporting" taille={15} />
            Relevé
          </button>
          <a
            className="bouton bouton--fantome bouton--compact"
            href={`/api/v1/reporting/mandats/${m.id}/positions.xlsx`}
          >
            Positions
          </a>
          <a
            className="bouton bouton--fantome bouton--compact"
            href={`/api/v1/reporting/mandats/${m.id}/transactions.xlsx`}
          >
            Transactions
          </a>
        </div>
      ),
    },
  ];

  return (
    <>
      <p className="entete__fil">Restitution</p>
      <div className="entete">
        <h1>Reporting trimestriel</h1>
        <span className="entete__ref">{tous.length} mandats actifs</span>
      </div>

      {erreur && (
        <BandeauAlerte nature="refus" titre="Chargement impossible">
          {messageErreur(erreur)}
        </BandeauAlerte>
      )}

      <Bloc titre="Période" meta="le relevé s’ouvre dans un onglet, prêt à imprimer">
        <div className="filtres">
          <div className="champ">
            <label className="surtitre" htmlFor="annee">
              Année
            </label>
            <input
              id="annee"
              type="number"
              min={2020}
              max={2100}
              value={annee}
              onChange={(e) => setAnnee(e.target.value)}
            />
          </div>
          <div className="champ">
            <label className="surtitre" htmlFor="trimestre">
              Trimestre
            </label>
            <select
              id="trimestre"
              className="champ__select"
              value={trimestre}
              onChange={(e) => setTrimestre(e.target.value)}
            >
              <option value="1">T1 — janv. à mars</option>
              <option value="2">T2 — avr. à juin</option>
              <option value="3">T3 — juil. à sept.</option>
              <option value="4">T4 — oct. à déc.</option>
            </select>
          </div>
        </div>
      </Bloc>

      <Bloc
        titre="Mandats"
        meta={
          chargement
            ? 'chargement…'
            : filtresActifs
              ? `${mandats.length} sur ${tous.length}`
              : undefined
        }
        actions={
          <a
            className="bouton bouton--icone bouton--compact"
            href="/api/v1/reporting/extraction/mandats.xlsx"
          >
            <Icone nom="reporting" taille={15} />
            Extraction Excel
          </a>
        }
      >
        <BarreFiltres
          actifs={filtresActifs}
          onRetirer={() => {
            setRecherche('');
            setProfil('');
            setAgence('');
          }}
        >
          <FiltreRecherche
            valeur={recherche}
            onChange={setRecherche}
            placeholder="Référence, nom ou prénom du client"
          />
          <FiltreChoix
            libelle="Profil"
            tous="Tous les profils"
            valeur={profil}
            onChange={setProfil}
            options={optionsDistinctes(tous, (m) => [m.profil.id, m.profil.libelle])}
          />
          <FiltreChoix
            libelle="Agence"
            tous="Toutes les agences"
            valeur={agence}
            onChange={setAgence}
            options={optionsDistinctes(tous, (m) => [
              m.agence.code,
              `${m.agence.code} — ${m.agence.libelle}`,
            ])}
          />
        </BarreFiltres>
        <Tableau
          colonnes={colonnes}
          lignes={mandats}
          cle={(m) => m.id}
          vide={filtresActifs ? 'Aucun mandat ne répond à ces critères.' : 'Aucun mandat actif.'}
        />
      </Bloc>
    </>
  );
}
